<script>
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    Avatar,
    Spinner,
    EmptyState,
    NextButton,
  },
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
    onClickNotification: {
      type: Function,
      default: () => {},
    },
    onMarkAllDoneClick: {
      type: Function,
      default: () => {},
    },
    onSortChange: {
      type: Function,
      default: () => {},
    },
    onMarkReadNotification: {
      type: Function,
      default: () => {},
    },
    onMarkUnreadNotification: {
      type: Function,
      default: () => {},
    },
    onSnoozeNotification: {
      type: Function,
      default: () => {},
    },
    onDeleteNotification: {
      type: Function,
      default: () => {},
    },
    sortOrder: {
      type: String,
      default: 'desc',
    },
  },
  computed: {
    ...mapGetters({
      notificationMetadata: 'notifications/getMeta',
    }),
    showEmptyResult() {
      return !this.isLoading && this.notifications.length === 0;
    },
    unreadSummary() {
      return this.$t('NOTIFICATIONS_PAGE.UNREAD_SUMMARY', {
        count: this.notificationMetadata.unreadCount || 0,
      });
    },
    sortOptions() {
      return [
        {
          value: 'desc',
          label: this.$t('NOTIFICATIONS_PAGE.SORT.NEWEST'),
        },
        {
          value: 'asc',
          label: this.$t('NOTIFICATIONS_PAGE.SORT.OLDEST'),
        },
      ];
    },
  },
  methods: {
    dynamicTime,
    isUnread(notificationItem) {
      return notificationItem.read_at === null;
    },
    notificationTitle(notificationItem) {
      if (notificationItem.primary_actor) {
        return `#${notificationItem.primary_actor.id}`;
      }

      return this.$t('NOTIFICATIONS_PAGE.DELETE_TITLE');
    },
    notificationTypeLabel(notificationType) {
      const labels = {
        conversation_creation: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.conversation_creation'
        ),
        conversation_assignment: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.conversation_assignment'
        ),
        assigned_conversation_new_message: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.assigned_conversation_new_message'
        ),
        participating_conversation_new_message: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.participating_conversation_new_message'
        ),
        conversation_mention: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.conversation_mention'
        ),
        sla_missed_first_response: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.sla_missed_first_response'
        ),
        sla_missed_next_response: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.sla_missed_next_response'
        ),
        sla_missed_resolution: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.sla_missed_resolution'
        ),
        delivery_ledger_app_notification: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.delivery_ledger_app_notification'
        ),
      };

      return (
        labels[notificationType] ||
        this.$t('NOTIFICATIONS_PAGE.TYPE_LABEL.unknown')
      );
    },
    readToggleLabel(notificationItem) {
      return this.isUnread(notificationItem)
        ? this.$t('NOTIFICATIONS_PAGE.ROW_ACTIONS.MARK_READ')
        : this.$t('NOTIFICATIONS_PAGE.ROW_ACTIONS.MARK_UNREAD');
    },
    onReadToggle(notificationItem) {
      if (this.isUnread(notificationItem)) {
        this.onMarkReadNotification(notificationItem);
        return;
      }

      this.onMarkUnreadNotification(notificationItem);
    },
  },
};
</script>

<template>
  <section
    class="flex h-full grow shrink flex-col overflow-auto rounded-t-lais border border-b-0 border-lais-border bg-white px-4 py-4 shadow-lais-card sm:px-6"
  >
    <div
      class="mb-5 flex flex-col gap-4 border-b border-lais-border-soft pb-4 md:flex-row md:items-center md:justify-between"
    >
      <div>
        <h6 class="font-redHatDisplay text-[18px] font-semibold text-lais-text">
          {{ $t('NOTIFICATIONS_PAGE.HEADER') }}
        </h6>
        <p class="mt-1 text-[13px] text-lais-text-body">
          {{ unreadSummary }}
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <div
          class="inline-flex rounded-full border border-lais-border bg-white p-1"
          role="group"
          :aria-label="$t('NOTIFICATIONS_PAGE.SORT.LABEL')"
        >
          <button
            v-for="option in sortOptions"
            :key="option.value"
            type="button"
            class="rounded-full px-3 py-1 text-[12px] font-semibold transition-all"
            :class="
              sortOrder === option.value
                ? 'bg-lais-primary text-white'
                : 'text-lais-text-muted hover:text-lais-text'
            "
            @click="onSortChange(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <NextButton
          v-if="notificationMetadata.unreadCount"
          type="submit"
          sm
          :label="$t('NOTIFICATIONS_PAGE.MARK_ALL_DONE')"
          :is-loading="isUpdating"
          @click="onMarkAllDoneClick"
        />
      </div>
    </div>
    <div
      v-show="!isLoading"
      class="overflow-hidden rounded-lais border border-lais-border bg-white/70"
    >
      <div
        v-for="notificationItem in notifications"
        :key="notificationItem.id"
        role="button"
        tabindex="0"
        class="flex w-full flex-col gap-3 border-b border-lais-border-soft px-4 py-4 text-left transition-colors hover:bg-white sm:flex-row sm:items-start sm:justify-between last:border-b-0"
        :class="
          notificationItem.read_at === null
            ? 'border-l-4 border-l-lais-gold bg-lais-primary-50/20'
            : 'border-l-4 border-l-transparent'
        "
        @click="() => onClickNotification(notificationItem)"
        @keydown.enter.prevent="() => onClickNotification(notificationItem)"
        @keydown.space.prevent="() => onClickNotification(notificationItem)"
      >
        <div class="flex min-w-0 flex-1 items-start gap-3">
          <div class="mt-1 shrink-0">
            <div
              v-if="!notificationItem.read_at"
              class="size-2.5 rounded-full bg-lais-gold"
            />
            <div v-else class="size-2.5 rounded-full bg-lais-border" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="mb-1 flex flex-wrap items-center gap-2">
              <h5 class="m-0 text-[14px] font-semibold text-lais-text">
                {{ notificationTitle(notificationItem) }}
              </h5>
              <span
                class="inline-flex rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-lais-primary"
              >
                {{ notificationTypeLabel(notificationItem.notification_type) }}
              </span>
            </div>
            <p class="text-[13px] leading-6 text-lais-text-body sm:truncate">
              {{ notificationItem.push_message_title }}
            </p>
          </div>
        </div>

        <div class="flex shrink-0 items-center gap-3 sm:pl-4">
          <Avatar
            v-if="notificationItem.primary_actor.meta.assignee"
            :src="notificationItem.primary_actor.meta.assignee.thumbnail"
            :size="28"
            :name="notificationItem.primary_actor.meta.assignee.name"
            rounded-full
          />
          <span class="text-[12px] text-lais-text-muted">
            {{ dynamicTime(notificationItem.last_activity_at) }}
          </span>
          <div class="flex items-center gap-1">
            <button
              v-tooltip.top="readToggleLabel(notificationItem)"
              type="button"
              class="grid size-8 place-items-center rounded-full border border-lais-border bg-white text-lais-text-muted transition-all hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
              :aria-label="readToggleLabel(notificationItem)"
              :disabled="isUpdating"
              @click.stop="onReadToggle(notificationItem)"
            >
              <span
                class="size-4"
                :class="
                  isUnread(notificationItem)
                    ? 'i-lucide-mail-open'
                    : 'i-lucide-mail'
                "
              />
            </button>
            <button
              v-tooltip.top="
                $t('NOTIFICATIONS_PAGE.ROW_ACTIONS.SNOOZE_TOMORROW')
              "
              type="button"
              class="grid size-8 place-items-center rounded-full border border-lais-border bg-white text-lais-text-muted transition-all hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
              :aria-label="$t('NOTIFICATIONS_PAGE.ROW_ACTIONS.SNOOZE_TOMORROW')"
              :disabled="isUpdating"
              @click.stop="onSnoozeNotification(notificationItem)"
            >
              <span class="i-lucide-clock-3 size-4" />
            </button>
            <button
              v-tooltip.top="$t('NOTIFICATIONS_PAGE.ROW_ACTIONS.DELETE')"
              type="button"
              class="grid size-8 place-items-center rounded-full border border-[#f0d4d4] bg-white text-[#b42318] transition-all hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-50"
              :aria-label="$t('NOTIFICATIONS_PAGE.ROW_ACTIONS.DELETE')"
              :disabled="isUpdating"
              @click.stop="onDeleteNotification(notificationItem)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showEmptyResult"
      class="flex min-h-[220px] items-center justify-center rounded-lais border border-dashed border-lais-border bg-white px-6 text-center"
    >
      <EmptyState :title="$t('NOTIFICATIONS_PAGE.LIST.404')" />
    </div>
    <div v-if="isLoading" class="notifications--loader">
      <Spinner />
      <span>{{ $t('NOTIFICATIONS_PAGE.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.notifications--loader {
  @apply flex items-center justify-center p-10 text-base text-lais-text-body;
}
</style>
